import { logError } from "@lib/logger";

const DEFAULT_INTERVAL_MS = 3_000;

export type Fetch<TState, TFetchResult> = (state: TState) => Promise<TFetchResult>;
export type MergeFetchResultIntoState<TState, TFetchResult> = (state: TState, fetchResult: TFetchResult) => TState;
export type OnPollingStarted = () => void;
export type OnPollingStopped<TState> = (state: TState, consecutiveFailureCount: number) => void;

export class IntervalPoller<TState, TFetchResult> {
	private isPollRequestInFlight: boolean = false;
	private readonly fetch: Fetch<TState, TFetchResult>;
	private readonly mergeFetchResultIntoState: MergeFetchResultIntoState<TState, TFetchResult>;
	private readonly onPollingStarted: OnPollingStarted;
	private readonly onPollingStopped: OnPollingStopped<TState>;
	private readonly pollDescription: string;
	private readonly intervalMs: number = DEFAULT_INTERVAL_MS;
	private intervalId: ReturnType<typeof setInterval> | null = null;
	private state: TState;
	private consecutiveFailureCount = 0;
	private totalUpdateRequestsCount = 0;

	constructor(
		fetch: Fetch<TState, TFetchResult>,
		mergeFetchResultIntoState: MergeFetchResultIntoState<TState, TFetchResult>,
		onPollingStarted: OnPollingStarted,
		onPollingStopped: OnPollingStopped<TState>,
		state: TState,
		pollDescription: string
	) {
		this.fetch = fetch;
		this.mergeFetchResultIntoState = mergeFetchResultIntoState;
		this.onPollingStarted = onPollingStarted;
		this.onPollingStopped = onPollingStopped;
		this.state = state;
		this.pollDescription = pollDescription;
	}

	invalidateInFlightPollRequestAndResetSchedule(): void {
		this.totalUpdateRequestsCount += 1;
		this.isPollRequestInFlight = false;
		this.resetSchedule();
	}

	setState(state: TState): void {
		this.state = state;
	}

	start(): void {
		if (this.intervalId !== null) {
			return;
		}
		void this._pollOnce();
		this.intervalId = setInterval((): void => {
			void this._pollOnce();
		}, this.intervalMs);
	}

	stop(): void {
		if (this.intervalId !== null) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}

	resetSchedule(): void {
		if (this.intervalId === null) {
			return;
		}
		clearInterval(this.intervalId);
		this.intervalId = setInterval((): void => {
			void this._pollOnce();
		}, this.intervalMs);
	}

	private isUpdateRequestReceivedDuringPolling(updateRequestsAtPollStart: number): boolean {
		return this.totalUpdateRequestsCount !== updateRequestsAtPollStart;
	}

	private readonly _pollOnce = async (): Promise<void> => {
		if (this.isPollRequestInFlight) {
			return;
		}
		this.onPollingStarted();
		this.isPollRequestInFlight = true;
		const updateRequestsAtPollStart = this.totalUpdateRequestsCount;
		try {
			const fetchResult = await this.fetch(this.state);
			if (this.isUpdateRequestReceivedDuringPolling(updateRequestsAtPollStart)) {
				return;
			}
			this.consecutiveFailureCount = 0;
			this.state = this.mergeFetchResultIntoState(this.state, fetchResult);
			this.onPollingStopped(this.state, this.consecutiveFailureCount);
		} catch (error) {
			if (this.isUpdateRequestReceivedDuringPolling(updateRequestsAtPollStart)) {
				return;
			}
			this.consecutiveFailureCount += 1;
			logError(`Failed to poll ${this.pollDescription}`, { error, consecutiveFailureCount: this.consecutiveFailureCount });
			this.onPollingStopped(this.state, this.consecutiveFailureCount);
		} finally {
			if (!this.isUpdateRequestReceivedDuringPolling(updateRequestsAtPollStart)) {
				this.isPollRequestInFlight = false;
			}
		}
	};
}
